import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { login as authLogin, logout as authLogout, register as authRegister, getCurrentUser } from '../api/authApi';
import { setAccessTokenGetter, setWorkOSAuthenticated, isWorkOSEnabled } from '../api/workosAuth';
import { BYPASS_AUTH } from '../config/development';

const WORKOS_CLIENT_ID = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WORKOS_CLIENT_ID as string | undefined)?.trim() ?? '';
if (!import.meta.env.DEV && (!WORKOS_CLIENT_ID || !WORKOS_CLIENT_ID.trim())) {
  console.error(
    'VITE_WORKOS_CLIENT_ID is not set — authentication will not work in production. Add this to your Vercel environment variables.'
  );
}

interface User {
  id: string;
  username: string;
  email: string;
  fullName: string;
  role: string;
  organizationId: string;
  organizationName: string;
  // Optional additional properties
  name?: string;
  profile_image?: string;
  first_name?: string;
  last_name?: string;
}

interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (
    username: string,
    email: string,
    password: string,
    fullName: string,
    role: string,
    organizationName: string,
    organizationType: string,
    organizationAction: string
  ) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Simple dev user for development
const DEV_USER: User = {
  id: 'dev-user',
  username: 'developer',
  email: 'dev@example.com',
  fullName: 'Development User',
  role: 'admin',
  organizationId: 'dev-org',
  organizationName: 'Development Organization'
};

/** Map WorkOS user to app User shape for useUserRole/usePermission. */
function mapWorkOSUserToAppUser(wo: { id: string; email?: string | null; first_name?: string | null; last_name?: string | null; [key: string]: unknown }): User {
  const fullName = [wo.first_name, wo.last_name].filter(Boolean).join(' ') || (wo.email ?? '').split('@')[0] || 'User';
  const email = wo.email ?? '';
  const username = email ? email.split('@')[0] ?? String(wo.id) : String(wo.id);
  return {
    id: String(wo.id),
    username,
    email,
    fullName,
    role: 'user',
    organizationId: '',
    organizationName: '',
  };
}

type AuthLike = ReturnType<typeof useAuth> | null;

// WorkOS AuthKit is the app's user management. When WorkOS is not configured (e.g. local dev without .env),
// a dev-only fallback (BYPASS_AUTH or legacy token) is used.
const InnerUserProvider: React.FC<{ auth: AuthLike; children: React.ReactNode }> = ({ auth, children }) => {
  const useWorkOS = auth !== null;
  const [devFallbackUser, setDevFallbackUser] = useState<User | null>(BYPASS_AUTH ? DEV_USER : null);
  const [devFallbackLoading, setDevFallbackLoading] = useState(!BYPASS_AUTH && !useWorkOS);
  const [backendUserOverrides, setBackendUserOverrides] = useState<Partial<User> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Register WorkOS token getter so API clients can attach Bearer token
  useEffect(() => {
    if (!useWorkOS || !auth?.getAccessToken) return;
    setAccessTokenGetter(() => auth.getAccessToken().then((t) => t ?? null));
    return () => setAccessTokenGetter(() => Promise.resolve(null));
  }, [useWorkOS, auth]);

  // Sync auth state for authApi.isAuthenticated()
  useEffect(() => {
    if (!useWorkOS || !auth) return;
    setWorkOSAuthenticated(!!auth.user);
  }, [useWorkOS, auth]);

  // Fetch /auth/me so role and id come from backend (DB) for nav/guards
  useEffect(() => {
    if (!useWorkOS || !auth?.user) {
      if (useWorkOS && !auth?.user) setBackendUserOverrides(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await getCurrentUser();
        if (!cancelled && me) {
          setBackendUserOverrides({
            id: me.id,
            username: me.username,
            email: me.email,
            fullName: me.fullName,
            role: me.role,
            organizationId: me.organizationId,
            organizationName: me.organizationName,
          });
        }
      } catch {
        if (!cancelled) setBackendUserOverrides(null);
      }
    })();
    return () => { cancelled = true; };
  }, [useWorkOS, auth?.user]);

  // Dev fallback: fetch current user only when WorkOS is not configured (e.g. local dev)
  useEffect(() => {
    if (BYPASS_AUTH || useWorkOS) {
      if (useWorkOS) setDevFallbackLoading(false);
      return;
    }
    let cancelled = false;
    const checkAuth = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (!cancelled) setDevFallbackUser(currentUser);
      } catch {
        if (!cancelled) setDevFallbackUser(null);
      } finally {
        if (!cancelled) setDevFallbackLoading(false);
      }
    };
    checkAuth();
    return () => { cancelled = true; };
  }, [useWorkOS]);

  const user: User | null = useWorkOS && auth
    ? (auth.user
        ? { ...mapWorkOSUserToAppUser(auth.user as { id: string; email?: string | null; first_name?: string | null; last_name?: string | null }), ...backendUserOverrides }
        : null)
    : devFallbackUser;
  const isLoading = useWorkOS && auth ? auth.isLoading : devFallbackLoading;

  const login = async (email: string, password: string) => {
    if (useWorkOS && auth) {
      await auth.signIn();
      return;
    }
    if (BYPASS_AUTH) {
      setDevFallbackUser(DEV_USER);
      return;
    }
    try {
      setDevFallbackLoading(true);
      setError(null);
      const userData = await authLogin(email, password);
      setDevFallbackUser(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setDevFallbackLoading(false);
    }
  };

  const logout = async () => {
    if (useWorkOS && auth) {
      await auth.signOut();
      return;
    }
    if (BYPASS_AUTH) {
      setDevFallbackUser(null);
      return;
    }
    try {
      setDevFallbackLoading(true);
      setError(null);
      await authLogout();
      setDevFallbackUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logout failed');
      throw err;
    } finally {
      setDevFallbackLoading(false);
    }
  };

  const register = async (
    username: string,
    email: string,
    password: string,
    fullName: string,
    role: string,
    organizationName: string,
    organizationType: string,
    organizationAction: string
  ) => {
    if (useWorkOS && auth) {
      await auth.signUp();
      return;
    }
    try {
      setDevFallbackLoading(true);
      setError(null);
      if (BYPASS_AUTH) {
        const mockUser: User = {
          id: '1',
          username,
          email,
          fullName,
          role,
          organizationId: 'dev-org',
          organizationName: organizationName,
        };
        setDevFallbackUser(mockUser);
        return;
      }
      const userData = await authRegister(
        username,
        email,
        password,
        fullName,
        role,
        organizationName,
        organizationType,
        organizationAction
      );
      setDevFallbackUser(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
      throw err;
    } finally {
      setDevFallbackLoading(false);
    }
  };

  const updateProfile = async (data: Partial<User>) => {
    try {
      setError(null);
      if (useWorkOS) {
        setBackendUserOverrides((prev) => ({ ...prev, ...data }));
      } else if (devFallbackUser) {
        setDevFallbackUser({ ...devFallbackUser, ...data });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Profile update failed');
      throw err;
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    register,
    updateProfile,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const UserProviderWithWorkOS: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  return <InnerUserProvider auth={auth}>{children}</InnerUserProvider>;
};

const UserProviderWithoutWorkOS: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <InnerUserProvider auth={null}>{children}</InnerUserProvider>;
};

/** Default: backend-driven auth; token from redirect fragment (sessionStorage). No AuthKit getter. */
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <InnerUserProvider auth={null}>{children}</InnerUserProvider>;
};

// eslint-disable-next-line react-refresh/only-export-components -- useUser is hook, UserProvider is component
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}; 